import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { AlertTriangle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ResolveWarehousesResponse, ResolvedSeller } from '@/lib/types/feature/seller-tag';
import { downloadCSV } from '@/lib/utils';

interface SelectedSellersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** どのタグで選んだか。見出しに出す */
  sourceLabel: string;
  result: ResolveWarehousesResponse;
  /** CSVのファイル名に使う */
  accountCode: string;
  /** 適用後に倉庫ツリーを手で触ったか。触っていればタグの内容とズレている */
  manuallyChanged: boolean;
}

/** 店舗1件をCSVの1行にする */
function toCsvRow(seller: ResolvedSeller, status: string) {
  return {
    店舗ID: seller.seller_customer_id,
    店舗名: seller.seller_name,
    店舗階層: seller.seller_hierarchy,
    対象倉庫: seller.warehouses.map((w) => w.name).join(' / '),
    状態: status,
  };
}

/**
 * タグで選ばれた店舗の確認ダイアログ（FKV3-13004 ①）
 *
 * 倉庫ツリーには倉庫名しか出ず、倉庫名と店舗名が一致する保証もないため、
 * これが無いと「このタグで何が選ばれたか」を店舗の粒度で検証できない。
 */
export function SelectedSellersDialog({
  open,
  onOpenChange,
  sourceLabel,
  result,
  accountCode,
  manuallyChanged,
}: SelectedSellersDialogProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return result.matched_sellers;
    return result.matched_sellers.filter((seller) =>
      [seller.seller_customer_id, seller.seller_name, seller.seller_hierarchy]
        .join(' ')
        .toLowerCase()
        .includes(keyword),
    );
  }, [result.matched_sellers, search]);

  // 対象0件でも、倉庫が無くて落ちた店舗があればその記録は落とせる必要がある
  const csvRowCount = result.matched_sellers.length + result.sellers_without_warehouse.length;

  const handleDownloadCSV = () => {
    // 対象外になった店舗も同じCSVに含める（「この条件で抽出した」記録として残すため）
    const rows = [
      ...result.matched_sellers.map((seller) => toCsvRow(seller, '対象')),
      ...result.sellers_without_warehouse.map((seller) =>
        toCsvRow(seller, '対象外（倉庫が紐づかない）'),
      ),
    ];
    const date = new Date().toISOString().split('T')[0];
    downloadCSV(rows, `対象店舗_${accountCode}_${date}.csv`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>対象店舗 {result.matched_sellers.length}件</DialogTitle>
          <DialogDescription>
            {sourceLabel}
            {manuallyChanged && '（適用後に倉庫ツリーを手動で変更しています）'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <TextField
            size="small"
            placeholder="店舗ID・店舗名・店舗階層で検索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 320 }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadCSV}
            disabled={csvRowCount === 0}
          >
            <Download className="mr-1 h-4 w-4" />
            CSVダウンロード
          </Button>
        </div>

        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 120 }}>店舗ID</TableCell>
                <TableCell sx={{ width: 200 }}>店舗名</TableCell>
                <TableCell>店舗階層</TableCell>
                <TableCell sx={{ width: 220 }}>対象倉庫</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    該当する店舗がありません
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((seller) => (
                  <TableRow key={seller.seller_id} hover>
                    <TableCell>{seller.seller_customer_id}</TableCell>
                    <TableCell>{seller.seller_name}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {seller.seller_hierarchy}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {seller.warehouses.map((w) => w.name).join(' / ')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* タグは付いているのに抽出対象から静かに落ちる唯一のケースなので必ず別枠で出す */}
        {result.sellers_without_warehouse.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
            <div className="flex items-center gap-1 font-medium text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              倉庫が紐づかず対象外になった店舗 {result.sellers_without_warehouse.length}件
            </div>
            <p className="mt-1 text-amber-900">
              {result.sellers_without_warehouse
                .map((s) => `${s.seller_customer_id} ${s.seller_name}`)
                .join(' ／ ')}
            </p>
          </div>
        )}

        {result.unknown_seller_count > 0 && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm">
            <div className="flex items-center gap-1 font-medium text-red-800">
              <AlertTriangle className="h-4 w-4" />
              タグは付いているが店舗マスタに存在しない店舗 {result.unknown_seller_count}件
            </div>
            <p className="mt-1 text-red-900">
              店舗が入れ替わってタグだけが残っている可能性があります。店舗タグ管理で付け直してください
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
